import dayjs from 'dayjs';
import get from 'lodash/get';
import { action, autorun, computed, makeObservable, observable } from 'mobx';

import { getRequest } from '../../services/api';
import { type ApiResponse, type IGetStoreResponse, type IOpenPaymentRequest } from '../../services/types';
import { TSafeChargePaymentType } from '../payredeem/types';
import { type RootStore } from '../RootStore';
import { type SubscriptionPlanEnhanced } from '../subscription/types';
import { EnumOpenDialog } from '../ui/types';

import { type IOffer, type IOrderPackage, type IStore, type IStorePackage, type ITransactionsData } from './types';

export class AppStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    autorun(() => {
      if (this.rootStore.authStore.authorized) {
        void this.fetchStorePackages();
      } else {
        this.setTransactions([]);
        this.storePackagesRegistry.clear();
        this.offerPackagesRegistry.clear();
        this.setStorePackagesFetched(false);
      }
    });
  }

  public storePackagesRegistry = new Map<number, IStorePackage | IOrderPackage>([]);
  public offerPackagesRegistry = new Map<number, IOffer>([]);

  @observable
  public currentOrderPackage: (IOrderPackage & { minigameId?: number; metagamesTokensAmount?: number }) | null = null;

  @action
  public setCurrentOrderPackage(
    event:
      | (IOrderPackage & { minigameId?: number; extraData?: Record<string, unknown>; metagamesTokensAmount?: number })
      | null,
  ) {
    this.currentOrderPackage = event;
  }

  @observable
  public storePackages: IStorePackage[] = [];

  @observable
  public offersPackagesIds: number[] = [];

  @observable
  public storePackagesFetched = false;

  @observable
  public currentStore: IStore | null = null;

  @observable
  public store: IStore | null = null;

  @observable
  private selectedPackageId: number | null = null;

  @observable
  public transactions: ITransactionsData[] = [];

  @observable
  public currentPaymentPackage: IOpenPaymentRequest | null = null;

  @observable
  public lowBalanceOffer: IOffer | null = null;

  @observable
  public welcomeOffer: IOffer | null = null;

  @action
  public setCurrentStore(store: IStore) {
    this.currentStore = store;
  }

  @action
  public setStorePackagesFetched(state: boolean) {
    this.storePackagesFetched = state;
  }

  @action
  private setStorePackages(storePackages: IStorePackage[]) {
    this.storePackages = storePackages;
  }

  @computed
  public get storePackagesIds() {
    return this.storePackages.map((storePkg) => storePkg.packageId);
  }

  @computed
  public get selectedPackage() {
    const packageItem = this.storePackagesRegistry.get(this.selectedPackageId || -1) || this.currentOrderPackage;
    if (packageItem) {
      return packageItem;
    }
    return null;
  }

  public async fetchStorePackages() {
    const response = await getRequest<ApiResponse<IGetStoreResponse>>('/v2/store', { limit: 9, page: 1 });

    const { store, npu_store: npuStore } = get(response, 'data.data');
    let currentStore = store;

    if (npuStore) {
      this.store = { ...store };
      currentStore = npuStore;
    }
    if (currentStore) {
      this.setCurrentStore(currentStore);
      currentStore.storePackages.forEach((storePkg) => {
        this.storePackagesRegistry.set(storePkg.packageId, storePkg);
      });
      this.setStorePackages(currentStore.storePackages);
      this.setStorePackagesFetched(true);
    }
  }

  public removeNpuStore() {
    if (!this.currentStore?.storeParameters?.isNpuStore || !this.store) return;
    this.storePackagesRegistry.clear();

    this.setCurrentStore(this.store);
    const storePackages: IStorePackage[] = [];
    this.store.storePackages.forEach((storePkg) => {
      this.storePackagesRegistry.set(storePkg.packageId, storePkg);
      storePackages.push(storePkg);
    });
    this.setStorePackages(storePackages);
    this.setStorePackagesFetched(true);
  }

  @action
  public setSelectedPackageId(packageId: number | null) {
    this.selectedPackageId = packageId;
  }

  @action
  public setCurrentPaymentPackage(data: IOpenPaymentRequest | null) {
    this.currentPaymentPackage = data;
  }

  @action
  public setTransactions(transactions: ITransactionsData[]) {
    this.transactions = transactions;
  }

  public async getTransactions() {
    try {
      const response = await getRequest('user/transactions', {
        actionType: 'all',
        startDate: dayjs().subtract(14, 'days').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        limit: 100,
        page: 1,
        version: 2,
      });
      this.setTransactions(get(response, 'data.data.rows', []) as ITransactionsData[]);
    } catch (e) {
      this.setTransactions([]);
    }
  }

  @action
  public handleOpenOrder(packageId: number, isMiniStore?: boolean, onClose?: VoidFunction) {
    this.setCurrentPaymentPackage({ packageId, paymentType: TSafeChargePaymentType.deposit });

    if (this.currentOrderPackage && this.currentOrderPackage.packageId === packageId) {
      this.storePackagesRegistry.set(packageId, this.currentOrderPackage);
    }

    const { firstName, city, address, zipCode, email, dateOfBirth, territory } = this.rootStore.userStore.profile || {};

    if (!firstName || !territory?.code || !city || !address || !zipCode || !email || !dateOfBirth) {
      this.rootStore.uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenPersonalDetailsDialog });
    } else {
      this.rootStore.uiStore.setOpenDialog({
        dialog: EnumOpenDialog.isOpenOrderSummaryDialog,
        options: { isMiniStore, onClose },
      });
    }
  }

  @action
  public handleOpenSubscriptionOrder(subscription: SubscriptionPlanEnhanced, onClose?: VoidFunction) {
    const { price, quantity = 1 } = subscription;
    const packageId = subscription.packageId;
    const subscriptionPackage = {
      packageId,
      price: price * quantity,
      gcAmount: 0,
      scAmount: 0,
      subscriptionData: subscription,
    };

    this.storePackagesRegistry.set(packageId, subscriptionPackage);
    this.setSelectedPackageId(packageId);

    this.setCurrentPaymentPackage({ packageId, paymentType: TSafeChargePaymentType.deposit });

    const { firstName, city, address, zipCode, email, dateOfBirth, territory } = this.rootStore.userStore.profile || {};

    if (!firstName || !territory?.code || !city || !address || !zipCode || !email || !dateOfBirth) {
      this.rootStore.uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenPersonalDetailsDialog });
    } else {
      this.rootStore.uiStore.setOpenDialog({
        dialog: EnumOpenDialog.isOpenOrderSummaryDialog,
        options: { onClose, isSubscription: true },
      });
    }
  }

  public marketingSequence: Map<string | number, VoidFunction> = new Map([]);

  public createMarketingSequence(
    openOffer: (offerId: number) => void,
    openDailyBonus?: VoidFunction,
    welcomeOffersFlag?: boolean,
  ) {
    this.marketingSequence.clear();
    if (typeof openDailyBonus === 'function' && this.rootStore.rewardsStore.dailyBonus?.isEntitledToBonus) {
      this.marketingSequence.set('daily-bonus', openDailyBonus);
    }
    if (typeof openOffer === 'function' && this.welcomeOffer) {
      const { packageId } = this.welcomeOffer;
      this.marketingSequence.set('welcome-offer', () => openOffer(packageId));
    }
    if (this.offersPackagesIds.length > 0 && welcomeOffersFlag) {
      for (const offerId of this.offersPackagesIds) {
        const offer = this.offerPackagesRegistry.get(offerId);
        if (offer) {
          this.marketingSequence.set(offer.packageId, () => openOffer(offerId));
        }
      }
    }
  }

  @action
  public cleanCurrentOrderPackage() {
    this.setCurrentOrderPackage(null);
    this.setSelectedPackageId(null);
    this.setCurrentPaymentPackage(null);
  }
}
